'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@shared/auth';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MultistepForm } from '../../../form-controls/MultistepForm/MultistepForm';
import { useStepTracking } from '../../hooks/useStepTracking';
import { MyAccountSheet } from '../../MyAccountSheet/MyAccountSheet';
import { useProfileEditSheet } from '../hooks/useProfileEditSheet';
import { type NameEditFormData, useNameEditFormSchema } from '../schema';
import { NameEditStep } from '../steps/NameEditStep';
import { VerificationInfoStep } from '../steps/VerificationInfoStep';
import type { NameEditSheetProps } from '../types';
import { BackHandler } from './BackHandler';

function NameEditSheet({ open, onOpenChange, trigger }: NameEditSheetProps) {
  const t = useTranslations('my_account.profile.profile_info');
  const [isFirstStep, setIsFirstStep] = useState(true);
  const { handleBackNavigation, registerBackHandler } = useProfileEditSheet();

  const { session } = useSession();
  const playerProfile = session.profile;
  const isLoadingProfile = session.status === 'loading';

  // Define the form schema
  const formSchema = useNameEditFormSchema();

  // Initialize the form with player profile data
  const form = useForm<NameEditFormData>({
    resolver: zodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: playerProfile?.firstName || '',
      lastName: playerProfile?.lastName || '',
    },
  });

  // Update form values when player profile data is loaded
  useEffect(() => {
    if (playerProfile && !isLoadingProfile) {
      form.reset({
        firstName: playerProfile.firstName,
        lastName: playerProfile.lastName,
      });
    }
  }, [playerProfile, isLoadingProfile, form]);

  const handleClose = () => {
    form.reset();
    setIsFirstStep(true);
    onOpenChange(false);
  };

  const handleComplete = () => {
    handleClose();
  };

  const handleBackClick = () => {
    handleBackNavigation(handleClose);
  };

  // Define steps (moved up to use before hook and early return)
  const steps = [
    {
      id: 'name-edit',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <NameEditStep
            initialData={{
              firstName: playerProfile?.firstName || '',
              middleName: playerProfile?.middleName || '',
              lastName: playerProfile?.lastName || '',
            }}
          />
        </>
      ),
      schema: formSchema,
      'data-track-container': 'Edit Name',
    },
    {
      id: 'verification-info',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <VerificationInfoStep firstName={playerProfile?.firstName || ''} onSkipVerification={handleClose} />
        </>
      ),
      schema: formSchema.pick({ firstName: true }),
      'data-track-container': 'Verify Name',
    },
  ];

  // Use step tracking hook (moved before early return)
  const { currentTrackContainer, onStepChange } = useStepTracking(steps);

  const handleStepChange = (currentIsFirstStep: boolean, currentIsLastStep: boolean, stepId: string) => {
    setIsFirstStep(currentIsFirstStep);
    onStepChange(currentIsFirstStep, currentIsLastStep, stepId);
  };

  // Don't render if no player profile data
  if (!playerProfile) {
    return null;
  }

  return (
    <MyAccountSheet
      trigger={trigger}
      title={t('name_edit.title')}
      open={open}
      onOpenChange={onOpenChange}
      showBackButton={!isFirstStep}
      onBack={handleBackClick}
      data-track-feature="My Account"
      data-track-container={currentTrackContainer}
    >
      <FormProvider {...form}>
        <MultistepForm
          steps={steps}
          showProgressBar={false}
          showNavigation={false}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
          className="flex flex-col flex-1 min-h-0 h-full"
        />
      </FormProvider>
    </MyAccountSheet>
  );
}

export { NameEditSheet };
